import { useState, useEffect, useRef, useCallback } from "react";
import { WS_URL, ORG_CODE } from "../config";
import { useKeycloak } from "@react-keycloak/web";

export const STATUS = {
  CONNECTING: "connecting",
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  ERROR: "error",
  UNAUTHORIZED: "unauthorized", // new: distinct from generic ERROR
};

function getInitiateChatbotUrl(wsUrl) {
  const url = new URL(wsUrl);
  url.protocol = url.protocol === "wss:" ? "https:" : "http:";
  url.pathname = url.pathname.replace(
    "/clarity_socket/chatbot-ws-stream",
    "/clarity_dc/checkUsers",
  );
    url.searchParams.delete("org_code");
  return url.toString();
}

export function useWebSocket() {
  const { keycloak, initialized } = useKeycloak();
  const [status, setStatus] = useState(STATUS.DISCONNECTED);
  const [messages, setMessages] = useState([]);
  const [chat_widget_icon, setchat_widget_icon] = useState("");
  const [data_assistant_icon, setdata_assistant_icon] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const wsRef = useRef(null);
  const roomIdRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const pendingMessageRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const verifyingRef = useRef(false); // guard against overlapping verify calls
  const MAX_RECONNECT = 5;

  const addMessage = useCallback((role, content, videoResult = null) => {
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        role,
        content,
        timestamp: new Date(),
        videoResult,
      },
    ]);
  }, []);

  const sendInitialQuery = useCallback((ws, chatRoomId) => {
    ws.send(
      JSON.stringify({
        type: "chat",
        message: "Who are you? and how you can help me? also add followup questions",
        chatroom_id: chatRoomId,
      }),
    );
  }, []);

  // Opens the actual WebSocket. Only called after token verification succeeds.
  const openSocket = useCallback((token) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    setStatus(STATUS.CONNECTING);
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "init", org_code: ORG_CODE }));
    };

    ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (data.type === "connected") {
        roomIdRef.current = data.roomId;
        reconnectAttemptsRef.current = 0;
        setStatus(STATUS.CONNECTED);
        setchat_widget_icon(data.chat_widget_icon);
        setdata_assistant_icon(data["data-assistant-icon"]);
        if (!hasInitializedRef.current) {
          hasInitializedRef.current = true;
          sendInitialQuery(ws, data.roomId);
        }
        if (pendingMessageRef.current) {
          const { text } = pendingMessageRef.current;
          pendingMessageRef.current = null;
          ws.send(
            JSON.stringify({
              type: "chat",
              message: text,
              chatroom_id: data.roomId,
            }),
          );
        }
        return;
      }

      if (data.type === "token") {
        setIsTyping(false);
        const content = data.message || data.text || data.content || "";
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.role === "assistant") {
            return [
              ...prev.slice(0, -1),
              { ...last, content: last.content + content },
            ];
          }
          return [
            ...prev,
            {
              id: Date.now() + Math.random(),
              role: "assistant",
              content,
              timestamp: new Date(),
            },
          ];
        });
        return;
      }

       if (data.type === "chat_response") {
        setIsTyping(false);
        const content = data.message || data.text || data.content || "";
        const videoResult =
          data.video_result?.status && data.video_result?.data?.length > 0
            ? data.video_result.data
            : null;
        const followUpQuestions =
          Array.isArray(data.follow_up_questions) &&
          data.follow_up_questions.length > 0
            ? data.follow_up_questions
            : null;
        const sourceFileData =
          Array.isArray(data.source_file_data) && data.source_file_data.length > 0
            ? data.source_file_data
            : null;
        const imageResult =
          Array.isArray(data.image_result) && data.image_result.length > 0
            ? data.image_result
            : null;
        if (content) {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.role === "assistant") {
              return [
                ...prev.slice(0, -1),
                { ...last, content, videoResult, followUpQuestions, sourceFileData, imageResult },
              ];
            }
            return [
              ...prev,
              {
                id: Date.now() + Math.random(),
                role: "assistant",
                content,
                videoResult,
                followUpQuestions,
                sourceFileData,
                imageResult,
                timestamp: new Date(),
              },
            ];
          });
        }
        return;
      }

      if (data.type === "error") {
        setIsTyping(false);
        addMessage(
          "assistant",
          data.message || "An error occurred. Please try again.",
        );
        return;
      }
    };

    ws.onerror = () => {
      setStatus(STATUS.ERROR);
      setIsTyping(false);
    };

    ws.onclose = () => {
      setStatus(STATUS.DISCONNECTED);
      setIsTyping(false);
      roomIdRef.current = null;

      if (reconnectAttemptsRef.current < MAX_RECONNECT) {
        const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 30000);
        reconnectAttemptsRef.current += 1;
        // Reconnect goes through verification again, not straight to the socket
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      }
    };
  }, [addMessage, sendInitialQuery]);

  // New: verifies the token against checkUsers, then opens the socket if valid.
  const connect = useCallback(async () => {
    if (!WS_URL) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    if (verifyingRef.current) return; // avoid duplicate concurrent checks
    if (!initialized) return; // keycloak not ready yet

    const token = keycloak?.token || "";
    if (!token) {
      setStatus(STATUS.UNAUTHORIZED);
      return;
    }

    verifyingRef.current = true;
    setStatus(STATUS.CONNECTING);

    try {
      const apiUrl = getInitiateChatbotUrl(WS_URL);
      console.log(apiUrl,"apiUrl",WS_URL);
      
      const res = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.status) {
        verifyingRef.current = false;
        setStatus(STATUS.UNAUTHORIZED);
        return;
      }

      const data = await res.json();

      // Adjust this condition to match your API's actual response shape
      if (data?.status === false) {
        verifyingRef.current = false;
        setStatus(STATUS.UNAUTHORIZED);
        return;
      }

      verifyingRef.current = false;
      openSocket(token);
    } catch (err) {
      verifyingRef.current = false;
      setStatus(STATUS.ERROR);
    }
  }, [initialized, keycloak, openSocket]);

  const sendMessage = useCallback(
    (text) => {
      if (!text.trim()) return false;

      addMessage("user", text);
      setIsTyping(true);

      const ws = wsRef.current;
      const isOpen = ws && ws.readyState === WebSocket.OPEN;
      const hasRoom = !!roomIdRef.current;

      if (isOpen && hasRoom) {
        ws.send(
          JSON.stringify({
            type: "chat",
            message: text,
            chatroom_id: roomIdRef.current,
          }),
        );
      } else {
        pendingMessageRef.current = { text };
        connect();
      }

      return true;
    },
    [addMessage, connect],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    hasInitializedRef.current = false;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && roomIdRef.current) {
      hasInitializedRef.current = true;
      sendInitialQuery(ws, roomIdRef.current);
    }
  }, [sendInitialQuery]);

  useEffect(() => {
    if (!initialized) return; // wait for keycloak before first attempt
    connect();
    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectAttemptsRef.current = MAX_RECONNECT;
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect, initialized]);

  return {
    status,
    messages,
    isTyping,
    sendMessage,
    clearMessages,
    connect,
    STATUS,
    chat_widget_icon,
    data_assistant_icon,
  };
}